/* eslint-disable no-prototype-builtins,react-hooks/rules-of-hooks */
import React, { useState, useEffect } from 'react';
import { equals } from 'ramda';
import { withApollo, Query } from 'react-apollo';
import {
  shape, string, bool, func, arrayOf,
} from 'prop-types';

import {
  Dashboard as DashboardPage,
  Loading as LoadingPage,
  sizes,
  cagColors,
} from '@hyphengroup/cag-components';
import {
  withTracking,
  dashboardPromoBannerCtaClickEvent,
  dashboardLoginDashboardAnchorClickEvent,
  dashboardLoginDashboardViewallEvent,
  loginDashboardAllProductsModuleCtaClickEvent,
  loginDashboardCardCtaEvent,
  loginDashboardAllProductsModuleProductClickEvent,
  loginDashboardSendProducts,
  loginDashboardSendRewards,
  loginDashboardContentCard,
  loginDashboardContentCardSwipe,
  loginDashboardContentCardLoad,
  LOCATIONS,
  VERTICAL_CODE_FROM_PRODUCT_TYPENAME,
} from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import Layout from 'components/layout';
import { CustomerProfileContext } from 'containers/withCustomerProfileContext';
import {
  getComponentConfig,
  getStaticText,
  getDashboardDesktopMenuBar,
  getImages,
  getRuntimeEnvVariables,
} from 'helpers/Utils';
import Queries from 'queries';

import { withDashboardPage, withQuizSubmission } from 'containers/withQueries';
import moment from 'moment';
import {
  handleProductClick,
  modulesOrder,
} from 'helpers/dashboardUtils';
import {
  dashboardModules,
} from 'helpers/dashboardCommonUtils';
import usePageReloadOnHistoryNavigation from 'hooks/usePageReloadOnHistoryNavigation';
import usePromotions from 'components/dashboard/dashboardModules/reco';
import withQuiz from 'components/dashboard/dashboardModules/quiz';
import useRewards from 'components/dashboard/dashboardModules/rewards';
import withRewards from 'components/dashboard/dashboardModules/withRewards';
import { mapPolicyStatus as mapPAPolicyStatus } from './DashboardMoreInfoPA';
import { mapPolicyStatus as mapOBPolicyStatus } from './DashboardMoreInfoOB';

const customerProductTypes = {
  CustomerTiProduct: 'CustomerTiProduct',
  CustomerPaProduct: 'CustomerPaProduct',
  CustomerObProduct: 'CustomerObProduct',
  CustomerCcProduct: 'CreditCard',
  CustomerPlProduct: 'PersonalLoan',
  CustomerVanillaProduct: 'CustomerVanillaProduct',
};

const MAX_PRODUCT_LIMIT = 3;
const MAX_PRODUCT_NOLIMIT = 100;
const MAX_REWARDS_LIMIT = 3;
const MAX_REWARDS_NOLIMIT = 100;
const ALL_VERTICALS_FILTER = 'ALL';
const MIN_RECO_CARDS = 3;

export function Dashboard({
  history,
  match: { params },
  defaultLanguage,
  localisation,
  dashboardQuery,
  location,
  track,
  emptyProductsIcons,
  setShowQuizSelectionModal,
  toggleRewardPasswordModal,
}) {
  if (!dashboardQuery || dashboardQuery.loading) {
    return <LoadingPage />;
  }

  const language = params?.language || defaultLanguage;
  const {
    pageConfig: { footer, header } = {}, seoMetadata, refetch, customerProfile,
    articles, promotions, quizzes,
  } = dashboardQuery || {};

  const { logo } = header;
  const componentConfig = getComponentConfig(dashboardQuery);
  const staticText = getStaticText(dashboardQuery);
  const images = getImages(dashboardQuery);
  const allVerticals = Object.values(VERTICAL_CODE_FROM_PRODUCT_TYPENAME);
  const {
    showPasswordHasBeenChangedBanner,
  } = location.state || {};

  /** STATES */
  const [viewAllProducts, setViewAllProducts] = useState(false);
  const [viewAllRewards, setViewAllRewards] = useState(false);
  const [products, setProducts] = useState();
  const [rewards, setRewards] = useState();
  const [pageLoading] = usePageReloadOnHistoryNavigation();
  // eslint-disable-next-line eqeqeq
  const showArticles = getRuntimeEnvVariables('RUNTIME_ENABLE_DASHBOARD_ARTICLES') == 'true';

  const [banner, setBanner] = useState(null);
  useEffect(() => {
    if (showPasswordHasBeenChangedBanner) {
      setBanner({
        isShown: true,
        text: staticText.PASSWORD_SUCCESSFULLY_CHANGED_MESSAGE,
        type: 'success',
      });
      history.replace(location.pathname, {});
    }
  }, []);

  useEffect(() => {
    track({ data: { location: LOCATIONS.DASHBOARD } });
  }, []);

  const promotionsModule = usePromotions({
    promotions, quizzes, track, staticText, setShowQuizSelectionModal,
  });

  useEffect(() => {
    if (articles?.recommendations?.length
      && articles?.recommendations?.length >= MIN_RECO_CARDS
      && showArticles) {
      articles.recommendations.forEach((article) => {
        loginDashboardContentCardLoad(track, article);
      });
    }
  }, [articles]);

  if (pageLoading) {
    return <LoadingPage />;
  }

  function getFormatDate(date) {
    return moment(date).format('YYYY/MM/DD');
  }

  function getProductsData(baseURL) {
    const mapCustomerProductTypeToBadge = {
      [customerProductTypes.CustomerTiProduct]: {
        type: 'grassGreen',
        text: staticText.TI,
      },
      [customerProductTypes.CustomerCcProduct]: {
        type: 'skyBlue',
        text: staticText.CC,
      },
      [customerProductTypes.CustomerPlProduct]: {
        type: 'deepBlue',
        text: staticText.PL,
      },
      [customerProductTypes.CustomerPaProduct]: {
        type: 'lightYellow',
        text: staticText.PA,
      },
      [customerProductTypes.CustomerObProduct]: {
        type: 'gradientGreen',
        text: staticText.OB,
      },
    };

    return products?.filter(customerProduct => customerProduct.product).map(({
      __typename: productType,
      productId,
      product,
      status,
      obStatus,
      travelDefinition,
      policyStatus,
      ctaLink,
      policyStartDate,
      policyEndDate,
    }) => {
      let startDate;
      let endDate;
      let customerProductType = productType;
      if (productType === customerProductTypes.CustomerVanillaProduct) {
        const { __typename: customerProductType_ } = product;
        customerProductType = customerProductType_;
      }
      const badge = mapCustomerProductTypeToBadge[customerProductType];
      const vertical = VERTICAL_CODE_FROM_PRODUCT_TYPENAME[customerProductType];

      switch (customerProductType) {
        case customerProductTypes.CustomerTiProduct:
          startDate = getFormatDate(travelDefinition.startDate);
          endDate = getFormatDate(travelDefinition.endDate);

          return {
            badge,
            title: product.productName,
            subTitle: `${startDate} - ${endDate}`,
            status: policyStatus,
            statusLabel: staticText.STATUS_LABEL,
            bottomText: staticText[`PURCHASE_TI_STATUS_${policyStatus}`],
            displayButton: !!ctaLink,
            onCTAClick: () => {
              window.location.href = ctaLink;
              loginDashboardCardCtaEvent(
                track,
                VERTICAL_CODE_FROM_PRODUCT_TYPENAME[customerProductType],
                product
              );
            },
            onClick: () => {
              history.push(`${baseURL}/${staticText.DASHBOARD_PRODUCT_TI_ROUTE_NAME}/${productId}`);
              loginDashboardAllProductsModuleProductClickEvent(
                track,
                VERTICAL_CODE_FROM_PRODUCT_TYPENAME[customerProductType],
                product
              );
            },
            buttonText: staticText.BUY_ANOTHER_CTA,
          };
        case customerProductTypes.CustomerPaProduct:
          startDate = getFormatDate(policyStartDate);
          endDate = getFormatDate(policyEndDate);

          return {
            badge,
            title: product.productName,
            subTitle: `${startDate} - ${endDate}`,
            status: mapPAPolicyStatus[policyStatus],
            statusLabel: staticText.STATUS_LABEL,
            bottomText: staticText[`PURCHASE_PA_STATUS_${mapPAPolicyStatus[policyStatus]}`],
            onClick: () => {
              history.push(`${baseURL}/${staticText.DASHBOARD_PRODUCT_PA_ROUTE_NAME}/${productId}`);
              loginDashboardAllProductsModuleProductClickEvent(
                track,
                VERTICAL_CODE_FROM_PRODUCT_TYPENAME[customerProductType],
                product
              );
            },
          };
        case customerProductTypes.CustomerObProduct:
          startDate = getFormatDate(policyStartDate);
          endDate = getFormatDate(policyEndDate);

          return {
            badge,
            title: product.productName,
            subTitle: `${startDate} - ${endDate}`,
            status: mapOBPolicyStatus[obStatus],
            statusLabel: staticText.STATUS_LABEL,
            bottomText: staticText[`PURCHASE_DETAILS_OB_STATUS_${mapOBPolicyStatus[obStatus]}`],
            onClick: () => {
              history.push(`${baseURL}/${staticText.DASHBOARD_PRODUCT_OB_ROUTE_NAME}/${productId}`);
              loginDashboardAllProductsModuleProductClickEvent(
                track,
                VERTICAL_CODE_FROM_PRODUCT_TYPENAME[customerProductType],
                product && {
                  name: product.productName,
                  id: product.id,
                  brand: product.provider.providerName,
                  category: 'online-banking-insurance',
                }
              );
            },
          };
        case customerProductTypes.CustomerCcProduct:
        case customerProductTypes.CustomerPlProduct:
          return {
            badge,
            title: product.productName,
            bottomText: staticText[`STATUS_${status}`],
            status,
            onClick: handleProductClick && (() => {
              handleProductClick({
                history,
                productMoreInfoRoute: `${baseURL}/${staticText[`DASHBOARD_PRODUCT_${vertical}_ROUTE_NAME`]}/${productId}`,
              });
              loginDashboardAllProductsModuleProductClickEvent(
                track,
                VERTICAL_CODE_FROM_PRODUCT_TYPENAME[customerProductType],
                product,
              );
            }),
            buttonText: staticText.BUY_ANOTHER_CTA,
          };
        default: {
          return {};
        }
      }
    });
  }

  function getEmptyProducts() {
    return emptyProductsIcons.map(product => ({
      description: staticText[product],
      url: staticText[`${product}_LINK`],
      imgSrc: images && images[`${product}_MODULE_EMPTY`]?.image.url,
      onClick: () => loginDashboardAllProductsModuleCtaClickEvent(track, product),
      mobileWidth: product === 'PA' ? sizes(14) : null,
    }));
  }

  function getProductsAttentionInfo() {
    const cmsValues = [
      componentConfig?.DASHBOARD_PROMO_BANNER?.DASHBOARD_PROMO_BANNER_TITLE?.text,
      componentConfig?.DASHBOARD_PROMO_BANNER?.DASHBOARD_PROMO_BANNER_DESCRIPTION?.text,
      componentConfig?.DASHBOARD_PROMO_BANNER?.DASHBOARD_PROMO_BANNER_CTA?.text,
      componentConfig?.DASHBOARD_PROMO_BANNER?.DASHBOARD_PROMO_BANNER_CTA_LINK?.text,
    ];

    const hasAllRequiredCmsValues = cmsValues.every(val => !!val);

    if (!hasAllRequiredCmsValues) return null;

    return {
      title: componentConfig.DASHBOARD_PROMO_BANNER.DASHBOARD_PROMO_BANNER_TITLE.text,
      description: componentConfig.DASHBOARD_PROMO_BANNER.DASHBOARD_PROMO_BANNER_DESCRIPTION.text,
      cta: {
        content: componentConfig.DASHBOARD_PROMO_BANNER.DASHBOARD_PROMO_BANNER_CTA.text,
        onClick: () => {
          dashboardPromoBannerCtaClickEvent(track);
          window.location.href = componentConfig
            .DASHBOARD_PROMO_BANNER
            .DASHBOARD_PROMO_BANNER_CTA_LINK.text;
        },
      },
    };
  }

  function getProductsFilters() {
    const filters = [
      {
        val: ALL_VERTICALS_FILTER,
        name: staticText.DASHBOARD_PRODUCTS_MODULE_TITLE,
      },
    ];

    return filters;
  }

  function toggleViewAllProducts() {
    setViewAllProducts(!viewAllProducts);
  }

  const customerProductsQueryVariables = {
    language: localisation[language],
    verticals: allVerticals,
    productsLimit: viewAllProducts ? MAX_PRODUCT_NOLIMIT : MAX_PRODUCT_LIMIT,
  };

  const customerRewardsQueryVariables = {
    language: localisation[language],
    rewardsLimit: viewAllRewards ? MAX_REWARDS_NOLIMIT : MAX_REWARDS_LIMIT,
  };

  return (
    <Query
      query={Queries.GetCustomerProducts}
      variables={customerProductsQueryVariables}
      onCompleted={(data) => {
        const customerProducts = data?.customerProfile?.customerProducts || {};
        if (!equals(products, customerProducts?.products)) {
          setProducts(customerProducts?.products);
          loginDashboardSendProducts(track, customerProducts?.products);
        }
      }}
    >
      {({ data: productsData, loading: loadingProducts, refetch: refetchProducts }) => {
        if (!products && loadingProducts) return <LoadingPage />;

        return (
          <Query
            query={Queries.GetCustomerRewards}
            variables={customerRewardsQueryVariables}
            onCompleted={(data) => {
              const customerRewards = data?.customerProfile?.customerRewards || {};
              if (!equals(rewards, customerRewards?.reward)) {
                setRewards(customerRewards?.reward);
                loginDashboardSendRewards(track, customerRewards?.reward);
              }
            }}
          >
            {({ data: rewardsData, loading: loadingRewards, refetch: refetchRewards }) => {
              if (!rewards && loadingRewards) return <LoadingPage />;

              function refetchPage() {
                refetch();
                refetchProducts();
                refetchRewards();
              }

              function getDashboardModulesData(baseURL) {
                const modules = {};
                const modulesData = [];

                /** PRODUCTS MODULE */
                if (productsData?.customerProfile?.hasOwnProperty('customerProducts')) {
                  let viewAllProductsText = '';
                  const customerProducts = productsData?.customerProfile?.customerProducts || {};

                  if (customerProducts?.count > MAX_PRODUCT_LIMIT) {
                    viewAllProductsText = viewAllProducts
                      ? staticText.VIEW_LESS_CTA
                      : staticText.VIEW_ALL_CTA;
                  }

                  modules[dashboardModules.PRODUCTS] = {
                    id: dashboardModules.PRODUCTS,
                    menuTitle: staticText.PRODUCTS_MODULE_ANCHOR,
                    onAnchorClick: () => dashboardLoginDashboardAnchorClickEvent(track, 'products'),
                    products: {
                      viewAllText: viewAllProductsText,
                      onViewAllClick: () => { toggleViewAllProducts(); dashboardLoginDashboardViewallEvent(track, 'products'); },
                      emptyHeader: staticText.PRODUCT_MODULE_HEADER,
                      emptySubheader: staticText.PRODUCTS_MODULE_EMPTY_STATE_HEADER,
                      emptyDescription: staticText.PRODUCTS_MODULE_EMPTY_STATE_DESCRIPTION,
                      emptyProducts: getEmptyProducts(),
                      data: getProductsData(baseURL),
                      filters: getProductsFilters(),
                    },
                  };
                }

                modules[dashboardModules.PROMO_BANNER] = {
                  id: dashboardModules.PROMO_BANNER,
                  attentionInfo: getProductsAttentionInfo(),
                };

                /** PROMOTIONS RECOMMENDATIONS */
                modules[dashboardModules.PROMOTIONS] = promotionsModule;

                /** REWARDS MODULE */
                let viewAllRewardsText;
                const customerRewards = rewardsData?.customerProfile?.customerRewards || {};

                if (customerRewards?.count > MAX_REWARDS_LIMIT) {
                  viewAllRewardsText = viewAllRewards
                    ? staticText.VIEW_LESS_CTA
                    : staticText.VIEW_ALL_CTA;
                }

                const rewardsModule = useRewards({
                  ALL_VERTICALS_FILTER,
                  baseURL,
                  dashboardModules,
                  history,
                  images,
                  rewards,
                  setViewAllRewards,
                  staticText,
                  toggleRewardPasswordModal,
                  track,
                  viewAllRewards,
                  viewAllRewardsText,
                });

                modules[dashboardModules.REWARDS] = rewardsModule;

                // /** ARTICLES RECOMMENDATIONS */
                if (articles?.recommendations?.length
                  && articles?.recommendations?.length >= MIN_RECO_CARDS
                  && showArticles) {
                  const { recommendations } = articles;

                  modules[dashboardModules.ARTICLES] = {
                    id: dashboardModules.ARTICLES,
                    type: 'slider',
                    menuTitle: staticText.DASHBOARD_ARTICLES_TITLE,
                    articles: {
                      onSlideChange: (activeSlide) => {
                        loginDashboardContentCardSwipe(
                          track,
                          recommendations[activeSlide],
                        );
                      },
                      data: recommendations.map((recommendation) => ({
                        category: recommendation.category,
                        readDuration: `${recommendation.readDuration} ${staticText.DASHBOARD_ARTICLE_MIN_READ}`,
                        datePublished: recommendation.datePublished,
                        header: recommendation.header,
                        cta: {
                          text: recommendation.cta?.title,
                          onClick: () => {
                            loginDashboardContentCard(track, recommendation);
                            if (recommendation.cta?.href) {
                              const win = window.open(recommendation.cta.href, '_blank');
                              win.focus();
                            }
                          },
                        },
                      })),
                    },
                  };
                }

                modulesOrder.forEach((moduleName) => {
                  modules[moduleName] && modulesData.push(modules[moduleName]);
                });

                return modulesData;
              }

              return (
                <Layout
                  footer={footer}
                  header={header}
                  isNavPanelHidden
                  onlyForLoggedInUser
                  seoMetadata={seoMetadata}
                  customerProfile={customerProfile}
                  staticText={staticText}
                  images={images}
                  refetch={refetchPage}
                  variant="fullScreen"
                  style={{
                    backgroundColor: cagColors.all['bkground-01'],
                  }}
                >
                  <div>
                    <CustomerProfileContext.Consumer>
                      {({ showMobileMenu, showAccountMenu, baseURL }) => (
                        <DashboardPage
                            locale={localisation[language]}
                            onLeftIconClick={showMobileMenu}
                            onAccountIconClick={showAccountMenu}
                            siteLogo={{
                              link: logo?.link?.href,
                              imageTitle: logo?.title,
                              imageUrl: footer?.logo?.image?.url,
                            }}
                            sections={getDashboardModulesData(baseURL)}
                            banner={{
                              ...banner,
                              onClose: () => setBanner({}),
                            }}
                            desktopMenuBar={getDashboardDesktopMenuBar(staticText)}
                          />
                      )}
                    </CustomerProfileContext.Consumer>
                  </div>
                </Layout>
              );
            }}
          </Query>
        );
      }}
    </Query>
  );
}

Dashboard.defaultProps = {
  track: null,
};

Dashboard.propTypes = {
  track: func,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  dashboardQuery: shape({
    loading: bool.isRequired,
    pageConfig: shape({
      footer: shape({
        logo: shape({
          image: shape({
            url: string.isRequired,
          }).isRequired,
        }).isRequired,
      }).isRequired,
      header: shape({
        logo: shape({
          link: shape({
            href: string.isRequired,
          }).isRequired,
          title: string.isRequired,
        }).isRequired,
      }).isRequired,
    }),
  }).isRequired,
  match: shape({
    params: shape({
      language: string,
    }),
  }).isRequired,
  defaultLanguage: string.isRequired,
  localisation: shape({}).isRequired,
  location: shape({
    state: shape({
      showPasswordHasBeenChangedBanner: bool,
    }),
  }).isRequired,
  emptyProductsIcons: arrayOf(string),
  setShowQuizSelectionModal: func.isRequired,
  toggleRewardPasswordModal: func.isRequired,
};

export default withApplicationConfig(
  withTracking(
    withQuizSubmission(
      withDashboardPage(
        withApollo(
          withQuiz(
            withRewards(Dashboard)
          )
        )
      )
    )
  ),
  ['languages', 'contentstackKeys', 'dahsboard']
);
